import { useState, useMemo } from "react";
import { Users, Truck } from "lucide-react";
import CustomersTab from "@/components/dashboard/CustomersTab";
import VendorsTab from "@/components/dashboard/VendorsTab";

// Persist the last-picked view across tab switches / page reloads so the
// user doesn't get bounced back to Customers every time they open Contacts.
const STORAGE_KEY = "contacts.subTab";

const ContactsTab = ({ perms, onOpenProject, initialView }) => {
  const canCustomers = !!perms?.customers;
  const canVendors = !!perms?.vendors;
  const defaultView = useMemo(() => {
    if (initialView === "customers" && canCustomers) return "customers";
    if (initialView === "vendors" && canVendors) return "vendors";
    const stored = typeof window !== "undefined" ? window.localStorage?.getItem(STORAGE_KEY) : null;
    if (stored === "customers" && canCustomers) return "customers";
    if (stored === "vendors" && canVendors) return "vendors";
    return canCustomers ? "customers" : "vendors";
  }, [initialView, canCustomers, canVendors]);
  const [view, setView] = useState(defaultView);

  const setViewPersisted = (v) => {
    setView(v);
    try { window.localStorage?.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
  };

  const bothVisible = canCustomers && canVendors;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="contacts-tab">
      {bothVisible && (
        <div className="flex items-center justify-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 shadow-sm">
            <button
              type="button"
              onClick={() => setViewPersisted("customers")}
              data-testid="contacts-toggle-customers"
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === "customers"
                  ? "bg-[#1a3fd1] text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <Users className="h-3.5 w-3.5" /> Customers
            </button>
            <button
              type="button"
              onClick={() => setViewPersisted("vendors")}
              data-testid="contacts-toggle-vendors"
              className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === "vendors"
                  ? "bg-[#1a3fd1] text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              <Truck className="h-3.5 w-3.5" /> Vendors
            </button>
          </div>
        </div>
      )}
      <div className="min-h-0 flex-1">
        {view === "customers" && canCustomers ? (
          <CustomersTab onOpenProject={onOpenProject} />
        ) : view === "vendors" && canVendors ? (
          <VendorsTab />
        ) : null}
      </div>
    </div>
  );
};

export default ContactsTab;
